import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { tokenStorage } from '@api/lib/token-storage';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  name: string;
  /** Optional — not returned by OTP login flow */
  email?: string;
  mobile: string;
  role: string;
  permissions: string[];
  avatarUrl?: string;
  /** workshop system_code e.g. GW-XXXXX */
  code?: string;
  businessName?: string;
  business_type_id?: number;
}

export interface WorkshopContext {
  id: string;
  name: string;
  code: string;
  branchId: string;
  branchName: string;
  financialYearId: string;
  financialYearLabel: string;
  currency: string;
  locale: string;
  timezone: string;
}

// ─── State ────────────────────────────────────────────────────────────────────

interface AuthState {
  user: AuthUser | null;
  workshop: WorkshopContext | null;
  /** Access token — memory only, never persisted to localStorage. */
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthActions {
  setCredentials: (user: AuthUser, token: string) => void;
  setWorkshop: (workshop: WorkshopContext) => void;
  updateToken: (token: string) => void;
  /** Replace the in-memory permissions list (called on session restore). */
  setPermissions: (permissions: string[]) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  workshop: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialState,

        setCredentials: (user, token) =>
          set((state) => {
            state.user = user;
            state.accessToken = token;
            state.isAuthenticated = true;
            state.isLoading = false;
          }),

        setWorkshop: (workshop) =>
          set((state) => {
            state.workshop = workshop;
          }),

        updateToken: (token) =>
          set((state) => {
            state.accessToken = token;
          }),

        setPermissions: (permissions) =>
          set((state) => {
            if (state.user) state.user.permissions = permissions;
          }),

        logout: () =>
          set((state) => {
            Object.assign(state, { ...initialState, isLoading: false });
          }),

        setLoading: (loading) =>
          set((state) => {
            state.isLoading = loading;
          }),

        hasPermission: (permission) => {
          const { user } = get();
          if (!user) return false;
          if (user.role === 'Owner') return true;
          const perms = user.permissions?.length
            ? user.permissions
            : (tokenStorage.getPermissions() ?? []);
          return perms.includes('*') || perms.includes(permission);
        },

        hasAnyPermission: (permissions) => {
          const { user } = get();
          if (!user) return false;
          if (user.role === 'Owner') return true;
          const userPerms = user.permissions?.length
            ? user.permissions
            : (tokenStorage.getPermissions() ?? []);
          if (userPerms.includes('*')) return true;
          return permissions.some((p) => userPerms.includes(p));
        },
      })),
      {
        name: 'garagewale-auth',
        partialize: (state) => ({
          user: state.user,
          workshop: state.workshop,
        }),
        onRehydrateStorage: () => (state) => {
          if (state && !state.user) {
            state.isLoading = false;
          } else if (
            state?.user &&
            (!state.user.permissions || state.user.permissions.length === 0)
          ) {
            const cached = tokenStorage.getPermissions();
            state.user.permissions = cached?.length
              ? cached
              : state.user.role === 'Owner'
                ? ['*']
                : [];
          }
        },
      },
    ),
    { name: 'AuthStore' },
  ),
);
